/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useState, ReactNode, useEffect, useMemo, useRef } from "react";
import Toolbar from "@mui/material/Toolbar";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import MenuIcon from "@mui/icons-material/Menu";
import { Outlet, useLocation } from "react-router-dom";
import { ReactComponent as AirspanIcon } from "../../icons/airspan.svg";
import LoginPage from "../Login";
import { APP_BAR_TEXT, DIALOGOPENFOR } from "../../globals/globals";
import { useLayout } from "./hooks";
import { setSelectedGeoRegionIds, setSelectedGeoRegions } from "../../store/api/admin/reducers";
import { Navbar } from "../../components/Layout/Navbar";
import "../../components/Layout/Navigation.css";
import { Autocomplete, Badge, Box, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Menu, MenuItem, SxProps, TextField, Tooltip } from "@mui/material";
import AccountCircle from "@mui/icons-material/AccountCircle";
import HelpIcon from "@mui/icons-material/Help";
import MoreIcon from "@mui/icons-material/MoreVert";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
// AccountBox, LockReset, Logout icons moved to AccountMenu component
import { useStoreDispatch } from "../../store/hooks";
import { logout } from "../../store/api/auth/reducer";
import { menuProps, notificationSlotProps } from "../../components/helpers";
import { setBasicDialogOpenFor, setBasicPopoverAnchorEl, setOpenToast, setVersionData } from "../../store/helpers/helperReducers";
import BasicPopover from "../../components/Shared/Popover/BasicPopover";
// import useMediaQuery from "@mui/material/useMediaQuery";
import ConfigAlerts from "../../components/Notification/ConfigAlerts";
import { useSelector } from "react-redux";
import { activeMainTabSelector, basicDialogSelector, configAlertCountSelector } from "../../store/helpers/helperSelectors";
import { useConfigAlertData } from "../../components/Notification/hooks";
import ShowVersion from "../../components/Version/version";
import { useGetVersionMutation, useGetUserBySelfQuery } from "../../store/api/admin/adminApiSlice";
import { errorMessage } from "../../utils/helpers";
import { IGeoRegion } from "../../store/api/admin/types";
import BuildOutlined from "../../components/Shared/Icons/BuildOutlined";
import RedundancyBreak from "../../icons/RedundancyBreak.png"
import RedundancyConnect from "../../icons/RedundancyConnect.png"
import { useAuthForm } from "../../components/Auth/hooks";
import { useGetNodesQuery } from "../../store/api/devices/deviceApiSlice";
import { updateKeyword } from "../../store/search/reducer";
import ChangePassword from "../../components/Administrations/AccessControls/Users/ChangePassword/ChangePassword";
import { useGetSsoEnabledMutation, useLogoutAuditMutation } from "../../store/api/auth/authApiSlice";
import GeoRegionFilter from "../../components/Shared/GeoRegionFilter/GeoRegionFilter";
import { selectedGeoRegionIdsSelector } from "../../store/api/admin/selectors";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { RadixMultiSelect } from "../../components/ui/radix-multi-select";
import ThemeToggle from "../../components/Layout/ThemeToggle";
import AccountMenu from "../../components/Layout/AccountMenu";

const MiniVariantDrawer: React.FC = () => {
  const {
    navMenuItems,
    open,
    isLoggedIn,
    basicPopoverAnchorEl,
    geoRegions,
    selectedGeoRegion,
    handleGeoRegionClick,
    username,
    serverMaintenanceMode,
    handleOnMenuClick,
    isDashboardPath,
    isPerformancePath,
    isAdminPath,
    searchTooltip
  } = useLayout();
  const dispatch = useStoreDispatch();
  const basicDialogOpenFor = useSelector(basicDialogSelector);
  const activeMainTab = useSelector(activeMainTabSelector);
  const { statusData, statusError } = useAuthForm();
  useConfigAlertData(); // useing here to get every time latest config alerts on an interval
  //useNotificationData();
  const [logoutAudit] = useLogoutAuditMutation();
  const [getSsoEnabled] = useGetSsoEnabledMutation();
  //const bellIconNotification = "bellIconNotification";
  const cloudSyncIconOperation = "cloudSyncIconOperation";

  // const { data:configData, isLoading:isconfigLoading, error:configError } = useGetConfigurationQuery(undefined, { pollingInterval: API?.ENDPOINTS?.CONFIGURATION?.pollingInterval || 10000 });

  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = useState<null | HTMLElement>(null);
  const [enablePopoverFor, setEnablePopoverFor] = useState<ReactNode | undefined>(undefined);
  const [popoverIdFor, setPopoverIdFor] = useState<string | undefined>(undefined);
  const [openUserProfile, setOpenUserProfile] = React.useState(false);

  const selectedGeoRegionKeys = useSelector(selectedGeoRegionIdsSelector) || [];
  const hasRestoredRegions = useRef(false);

  const configAlertCount = useSelector(configAlertCountSelector);

  const [getVersion] = useGetVersionMutation();

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };



  const isMenuOpen = Boolean(anchorEl);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

  // Sort regions alphanumerically by name
  const sortedGeoRegions = useMemo(() => {
    if (!geoRegions || geoRegions.length === 0) return [];
    return [...geoRegions].sort((a, b) => a.name.localeCompare(b.name));
  }, [geoRegions]);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleRegionSelectionChange = (regionIds: string[]) => {
    dispatch(setSelectedGeoRegionIds(regionIds));
    const selectedRegions = sortedGeoRegions.filter((region: IGeoRegion) =>
      regionIds.includes(region.objectKey)
    );
    dispatch(setSelectedGeoRegions(selectedRegions));
  };
  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    handleMobileMenuClose();
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };
  const userProfileHandler = () => {
    setOpenUserProfile(true);
  };
  const handleChangePasswordClose = (reason: any) => {
    if (reason && reason._reactName === "onClick")
      return;

    setOpenUserProfile(false);
  };
  const logoutHandler = async () => {
    let redirectToSso = false;
    try {
      const ssoResponse: any = await getSsoEnabled();
      redirectToSso = ssoResponse?.data?.status === 200;
    } catch {
      // fall back to default
    }
    try {
      await logoutAudit({ username }).unwrap();
    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: error?.data ? JSON.stringify(error?.data) : errorMessage(error) }));
    }
    dispatch(logout());
    setAnchorEl(null);
    handleMobileMenuClose();
    if (redirectToSso) {
      window.location.replace("/v1/login/saml2/sso");
    } else {
      window.location.reload();
    }
  };

  const cloudSyncHandler = (event: React.MouseEvent<HTMLElement>) => {
    setPopoverIdFor(cloudSyncIconOperation);
    setEnablePopoverFor(<ConfigAlerts />);
    dispatch(setBasicPopoverAnchorEl(event.currentTarget));
  };

  const versionHandler = async (event: React.MouseEvent<HTMLElement>) => {
    try {
      const result: any = await getVersion();
      if (result?.error) throw result?.error?.message;
      if (result.data?.status === 200) {
        dispatch(setVersionData(result.data));
        dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.APPVERSION));
      }
    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  }

  useEffect(() => {
    if (statusData && (!statusData?.ha_state.startsWith("HA_A") && !statusData?.ha_state.startsWith("SA"))) {
      dispatch(setOpenToast({
        openToast: true,
        message: "Server configuration changed. You have been logged out from non-active server.",
        toastType: "warning"
      }));
      setTimeout(() => {
        dispatch(logout());
      }, 4000);
    }
  }, [dispatch, statusData, statusData?.ha_state]);

  useEffect(() => {
    if (!geoRegions || geoRegions.length === 0) return;

    if (selectedGeoRegionKeys.length > 0) {
      if (!hasRestoredRegions.current) {
        // IDs restored from localStorage — derive full region objects once
        hasRestoredRegions.current = true;
        const selectedRegions = sortedGeoRegions.filter((region: IGeoRegion) =>
          selectedGeoRegionKeys.includes(region.objectKey)
        );
        dispatch(setSelectedGeoRegions(selectedRegions));
      }
      return;
    }

    const allKeys = sortedGeoRegions.map((region: IGeoRegion) => region.objectKey);
    handleRegionSelectionChange(allKeys);
  }, [geoRegions, selectedGeoRegionKeys.length, sortedGeoRegions]);

  const { data: currentUser } = useGetUserBySelfQuery({ name: username }, { skip: !username });

  const accountMenuItems = useMemo(() => {
    const items: { label: string; icon: string; onClick: () => void; separatorAfter?: boolean }[] = [];
    // currentUser != null guards against two cases:
    // 1. undefined — query still loading (avoid flash of the option before we know auth type)
    // 2. null — user not found in the DB, which means they authenticated via SSO (SAML/RADIUS/TACACS+)
    //    and were never persisted to the users table. Without this check, !null?.externalAuth
    //    evaluates to true and Change Password would incorrectly appear for SSO users.
    if (username !== 'admin' && currentUser != null && !currentUser.externalAuth) {
      items.push({ label: 'Change Password', icon: 'lock_reset', onClick: userProfileHandler });
    }
    items.push({ label: 'Log out', icon: 'logout', onClick: logoutHandler });
    return items;
  }, [username, currentUser]);

  const popoverId = Boolean(basicPopoverAnchorEl) ? `${popoverIdFor}-popover` : undefined;

  const mobileMenuId = "account-menu-mobile";
  const renderMobileMenu = (
    <Menu
      // sx={{ top: "30px", left: "calc(100% - 185px)" }}
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
      slotProps={{ paper: { sx: { backgroundColor: "var(--card)" } } }}>
      <MenuItem onClick={cloudSyncHandler}>
        <IconButton size="large" aria-label="show 4 new mails" color="inherit">
          <Badge badgeContent={configAlertCount} color="error">
            <CloudSyncIcon color="primary" />
          </Badge>
        </IconButton>
        <p>Operations</p>
      </MenuItem>
      <MenuItem onClick={versionHandler} id="version">
        <IconButton size="large" aria-label="version" color="inherit">
          <HelpIcon color="primary" />
        </IconButton>
        <p>Version</p>
      </MenuItem>
      <MenuItem onClick={handleProfileMenuOpen}>
        <IconButton
          size="large"
          aria-label="account of current user"
          aria-controls="primary-search-account-menu"
          aria-haspopup="true"
          color="inherit">
          <AccountCircle color="primary" />
        </IconButton>
        <p>Profile</p>
      </MenuItem>
    </Menu>
  );

  const [devices, setDevices] = useState<any[]>([]);
  const [deviceSearchKeyword, setdeviceSearchKeyword] = useState("");
  const [deviceOpen, setDeviceOpen] = useState(false);

  const filters = new URLSearchParams();
  filters.set("size", "20");

  if (deviceSearchKeyword !== "" && deviceSearchKeyword.length > 0)
    filters.set("search", deviceSearchKeyword);
  const args = { filters: filters.toString() };
  const { data, isLoading, isFetching, refetch } = useGetNodesQuery(args);

  const regionMultiSelectorSxProps: SxProps = {
    width: 180,
    ml: 2.5,
    mr: 2.5,
    display: { xs: "none", md: "flex" },
    color: "var(--primary-foreground)",
    "& .MuiInputBase-root": {
      borderRadius: 1,
      boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
      cursor: "pointer",
      backgroundColor: "var(--primary)",
      "& fieldset": {
        borderColor: "var(--primary-foreground)",
      },
      "&:hover fieldset": {
        borderColor: "var(--primary-foreground)",
      },
    },
    "& .MuiSelect-select": {
      paddingTop: 0.75,
      paddingBottom: 0.75,
      color: "var(--primary-foreground)",
      backgroundColor: "var(--primary)",
    },
    "& .MuiSvgIcon-root": {
      color: "var(--primary-foreground)",
    },
    "& .MuiCheckbox-root.Mui-checked": {
      color: "primary.main",
    },
  };

  const [searchSubmitted, setSearchSubmitted] = React.useState(false);

  useEffect(() => {
    if (!searchSubmitted || isFetching) return;
    const results = data?.data || [];
    setDevices(results);
    if (results.length === 0) {
      setDeviceOpen(false);
      setSearchSubmitted(false);
    } else if (results.length === 1) {
      const device = results[0] as any;
      setDeviceOpen(false);
      console.log("Device:", results[0].name);
      dispatch(updateKeyword(results[0].name));
      handleGeoRegionClick(device.geoRegionName);
      setSearchSubmitted(false);
    } else {
      setDeviceOpen(true);
      setSearchSubmitted(false);
    }
  }, [data, isFetching, searchSubmitted, dispatch, handleGeoRegionClick]);

  const searchHandler = (e: any) => {
    if (deviceSearchKeyword === "" || deviceSearchKeyword.length === 0) {
      setDevices([]);
      setDeviceOpen(false);
      setSearchSubmitted(false);
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      setSearchSubmitted(true);
      refetch();
    }
  };

  const autocompleteSelectHandler = (event: any, newValue: any | null) => {
    if (newValue && newValue.objectKey) {
      setDeviceOpen(false);
      dispatch(updateKeyword(newValue.name));
      handleGeoRegionClick(newValue.geoRegionName);
    }
  };

  return (
    <>
      {/* <Dialog open={openUserProfile} onClose={handleChangePasswordClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ backgroundColor: "var(--card)" }}>Change Password</DialogTitle>
        <DialogContent sx={{ backgroundColor: "var(--card)" }}>
          <ChangePassword cancelHandler={() => setOpenUserProfile(false)} />
        </DialogContent>
      </Dialog>   */}
      {openUserProfile && (
        <ChangePassword cancelHandler={() => setOpenUserProfile(false)} />
      )}
      {isLoggedIn ? (
        <div className="flex flex-col h-full">
          <CssBaseline />
          <BasicPopover slotProps={notificationSlotProps} popoverId={popoverId}>
            {/* <ConfigAlerts /> */}
            {enablePopoverFor}
          </BasicPopover>
          {basicDialogOpenFor === DIALOGOPENFOR.APPVERSION && <ShowVersion />}
          <Navbar
            appName={APP_BAR_TEXT}
            logo={<AirspanIcon style={{ height: 16, width: 'auto' }} />}
            navItems={navMenuItems}
            currentSection={activeMainTab}
            onNavigate={handleOnMenuClick}
            backgroundColor={'var(--primary)'}
          >
            {!isDashboardPath && !isAdminPath && geoRegions && geoRegions.length === 1 && geoRegions[0] && "name" in geoRegions[0] && (
              <Typography component="div" sx={{ display: "flex", alignItems: "center" }}>
                {geoRegions[0].name}
              </Typography>
            )}
            {!isDashboardPath && (!geoRegions || !geoRegions.length) && (
              <Typography component="div" sx={{ display: "flex", alignItems: "center" }}>
                -
              </Typography>
            )}

            {statusData && (statusData?.ha_state === "HA_A" || statusData?.ha_state === "HA_AF") && (
              <>
                {statusData?.active2standby_connection_alive ? (
                  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", margin: "1rem" }}>
                    <Tooltip title="Redundancy Connected">
                      <img src={RedundancyConnect} alt="Redundancy Connect" />
                    </Tooltip></Box>
                ) : (
                  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", margin: "1rem" }}>
                    <Tooltip title="Standby Disconnected">
                      <img src={RedundancyBreak} alt="Redundancy Break" />
                    </Tooltip></Box>
                )}
              </>
            )}

            {isDashboardPath && (
              <Box>
                <Autocomplete
                  freeSolo
                  autoHighlight
                  title={searchTooltip}
                  onClose={() => setDeviceOpen(false)}
                  open={deviceOpen}
                  options={devices}
                  getOptionLabel={(device) => device.name || ""}
                  loading={isLoading}
                  onChange={autocompleteSelectHandler}
                  slotProps={{
                    paper: {
                      sx: {
                        backgroundColor: "var(--background)",
                        color: "var(--foreground)",
                        border: "1px solid var(--border)",
                        borderRadius: "calc(var(--radius) - 2px)",
                        boxShadow: "0 4px 6px -1px rgba(0,0,0,.1), 0 2px 4px -2px rgba(0,0,0,.1)",
                        mt: 0.6,
                        "& .MuiAutocomplete-option": {
                          fontSize: "0.875rem",
                          "&:hover, &[aria-selected='true']": {
                            backgroundColor: "var(--accent)",
                            color: "var(--accent-foreground)",
                          },
                        },
                        "& .MuiAutocomplete-noOptions, & .MuiAutocomplete-loading": {
                          fontSize: "0.875rem",
                          color: "var(--muted-foreground)",
                        },
                      },
                    },
                  }}
                  sx={{
                    width: { xs: 160, md: 180, lg: 200 },
                    mr: 1,
                    "& .MuiOutlinedInput-root": {
                      height: 36,
                      backgroundColor: "var(--background)",
                      borderRadius: "calc(var(--radius) - 2px)",
                      fontSize: "0.875rem",
                      padding: "0 12px !important",
                      "& fieldset": {
                        borderColor: "var(--input)",
                        borderWidth: "1px",
                      },
                      "&:hover fieldset": {
                        borderColor: "var(--ring)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "var(--ring)",
                        borderWidth: "1px",
                        boxShadow: "0 0 0 1px var(--ring)",
                      },
                      "& .MuiAutocomplete-input": {
                        padding: "0 !important",
                        color: "var(--foreground)",
                        "&::placeholder": {
                          color: "var(--muted-foreground)",
                          opacity: 1,
                        },
                      },
                      "& .MuiSvgIcon-root": {
                        color: "var(--muted-foreground)",
                        fontSize: 18,
                      },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.4rem",
                      color: "var(--muted-foreground)",
                      transform: "translate(14px, 8px) scale(1)",
                      "&.Mui-focused, &.MuiFormLabel-filled, &.MuiInputLabel-shrink": {
                        transform: "translate(14px, -9px) scale(0.75)",
                        color: "var(--foreground)",
                      },
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search"
                      size="small"
                      variant="outlined"
                      value={deviceSearchKeyword}
                      onChange={(e) => setdeviceSearchKeyword(e.target.value)}
                      onKeyDown={searchHandler}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isLoading ? <CircularProgress color="inherit" size={14} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Box>)}

            {!isAdminPath && geoRegions && geoRegions.length > 1 && (
              <GeoRegionFilter
                selectedRegionIds={selectedGeoRegionKeys}
                onSelectionChange={handleRegionSelectionChange}
              />
            )}

            <Tooltip title="Operations">
              <IconButton size="small" aria-label="show 4 new mails" color="inherit" onClick={cloudSyncHandler} sx={{ mx: 0.4 }}>
                <Badge badgeContent={configAlertCount} color="error">
                  <CloudSyncIcon sx={{ fontSize: 18 }} />
                </Badge>
              </IconButton>
            </Tooltip>
            <Tooltip title="Version">
              <IconButton size="small" aria-label="Version" color="inherit" onClick={versionHandler} sx={{ mx: 0.4 }}>
                <HelpIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <ThemeToggle />
            <AccountMenu username={username} items={accountMenuItems} />
            {/* </Box> */}
            <Box sx={{ display: { xs: "flex", md: "none" } }}>
              <IconButton
                size="small"
                aria-label="show more"
                aria-controls={mobileMenuId}
                aria-haspopup="true"
                onClick={handleMobileMenuOpen}
                color="inherit">
                <MoreIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Navbar>
          {renderMobileMenu}
          <main className="flex-1 overflow-auto" style={{ backgroundColor: "var(--background)" }}>
            {serverMaintenanceMode && (
              <div
                style={{
                  backgroundColor: "var(--warning)",
                  color: "var(--primary-foreground)",
                  padding: "0.5rem 0",
                  marginBottom: "1rem",
                  marginTop: "1rem",
                  position: "fixed",
                  top: "44px",
                  zIndex: "2",
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}>
                <BuildOutlined fill="var(--primary-foreground)" />
                <Typography sx={{ ml: 2 }} variant="body1">
                  AMS is in maintenance mode.
                </Typography>
              </div>
            )}
            <div style={{ marginBottom: serverMaintenanceMode ? "1.8rem" : "0", height: "56px" }} />
            <Outlet />
          </main>
        </div>
      ) : (
        <LoginPage />
      )
      }
    </>
  );
};

export default MiniVariantDrawer;